import { Formatter, NameType, Payload, ValueType } from "recharts/types/component/DefaultTooltipContent";
import { TooltipContentProps } from "recharts/types/component/Tooltip";

interface GraphTooltipProps<TValue extends ValueType, TName extends NameType>
    extends Pick<TooltipContentProps<TValue, TName>, "active" | "payload" | "label"> {
    formatter: Formatter<TValue, TName>;
    labelFormatter: (label: string) => string;
}

export const GraphTooltip = <TValue extends ValueType, TName extends NameType>({
    active,
    payload,
    label,
    formatter,
    labelFormatter,
}: GraphTooltipProps<TValue, TName>) => {
    if (!active || !payload || payload.length === 0) {
        return null;
    }

    return (
        <div className="bg-white border border-[#ccc] p-[10px] whitespace-nowrap">
            <p className="m-0">{labelFormatter(label)}</p>
            <ul className="p-0 m-0 list-none">
                {payload.map((entry: Payload<TValue, TName>, index: number) => {
                    const formatted = formatter(entry.value as TValue, entry.name as TName, entry, index, payload);
                    const [formattedValue, formattedName] = Array.isArray(formatted)
                        ? formatted
                        : [formatted, entry.name];

                    return (
                        <li key={entry.dataKey?.toString() ?? index} className="flex items-center gap-1.5 py-1">
                            <span
                                className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: entry.color }}
                            />
                            <span>
                                {formattedName}: {formattedValue}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};
